import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest, ApiError } from "../../../shared/api/client";
import { refreshIntervalMs, sortCalls, parseTechnicalBoardResponse, type TechnicalBoardCall, type TechnicalBoardTeamMember } from "./technical-board-model";
import { demoItems, demoTeam } from "./technical-board-demo";

export function useTechnicalBoardWorkspace() {
const demoMode = new URLSearchParams(window.location.search).get("demo") === "1";
const [items, setItems] = useState<TechnicalBoardCall[]>(() => demoMode ? demoItems() : []);
const [team, setTeam] = useState<TechnicalBoardTeamMember[]>(() => demoMode ? demoTeam : []);
const [now, setNow] = useState(() => Date.now());
const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(() => demoMode ? Date.now() : null);
const [loading, setLoading] = useState(!demoMode);
const [error, setError] = useState("");
const mounted=useRef(false), pending=useRef(false), generation=useRef(0);
const load = useCallback(async () => {
    if(!mounted.current || pending.current) return;
    if (demoMode) {
      setLastUpdatedAt(Date.now());
      return;
    }
    const token=generation.current;pending.current=true;
    try {
      const response = await apiRequest<unknown>(
        "/api/production/technical-calls/board",
        { cache: "no-store" },
      );
      if(!mounted.current || token!==generation.current) return;
      const data=parseTechnicalBoardResponse(response);
      setItems(data.items);
      setTeam(data.onDutyTeam ?? []);
      setLastUpdatedAt(data.generatedAt ? Date.parse(data.generatedAt) : Date.now());
      setError("");
    } catch (loadError) {
      if(!mounted.current || token!==generation.current) return;
      if (loadError instanceof ApiError && loadError.code === "TECHNICAL_MOBILE_SESSION_REQUIRED") {
        window.location.replace("/?technical=1");
        return;
      }
      setError(loadError instanceof Error ? loadError.message : "Teknik çağrılar alınamadı.");
    } finally {
      if(mounted.current && token===generation.current){pending.current=false;setLoading(false);}
    }
  }, [demoMode]);
useEffect(() => {
    mounted.current=true;generation.current++;
    void load();
    const refresh = window.setInterval(() => void load(), refreshIntervalMs);
    const updateClock = window.setInterval(() => setNow(Date.now()), 1_000);
    const onVisibilityChange = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      mounted.current=false;generation.current++;pending.current=false;
      window.clearInterval(refresh);
      window.clearInterval(updateClock);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load]);
const orderedItems = useMemo(() => sortCalls(items), [items]);
const criticalCall = orderedItems.find((item) => item.impact === "STOPPED" && !item.assignedTechnicianName) ?? orderedItems[0] ?? null;
const queuedItems = criticalCall ? orderedItems.filter((item) => item.id !== criticalCall.id) : orderedItems;
const stoppedCount = items.filter((item) => item.impact === "STOPPED").length;
const unassignedCount = items.filter((item) => !item.assignedTechnicianName).length;
const isStale = lastUpdatedAt !== null && now - lastUpdatedAt > refreshIntervalMs * 3;
const connectionProblem = Boolean(error) || isStale;
return { demoMode, items, team, now, lastUpdatedAt, loading, error, criticalCall, queuedItems, stoppedCount, unassignedCount, connectionProblem, load };
}
